import { jsx } from '@amazon/vinyl-tsx'
import type { ObservableValue } from '@amazon/vinyl-observable'
import type { I18nString } from '@amzn/bf-i18n'
import { Icon } from '@/components/Icon'
import type { IconName } from '@/components/Icons'
import { Link } from '@/components/Link'
import type { NavigateOptions } from '@/router/router'

type LocalizedText = ObservableValue<I18nString | ''>

// GH's ErrorContent variants share this cover graphic.
const ERROR_HEADPHONES_LOGO_URL =
    'https://m.media-amazon.com/images/G/01/digital/music/player/recs-error-headphones'

export type ErrorSurfaceAction =
    | {
          readonly href: string
          readonly label: LocalizedText
          readonly navigateOptions?: NavigateOptions
      }
    | { readonly label: LocalizedText; readonly onClick: () => void }

export interface ErrorSurfaceProps {
    readonly message: LocalizedText
    readonly detail?: LocalizedText
    readonly actions: readonly ErrorSurfaceAction[]
    readonly centered?: boolean
    /** The centered surface's icon; a warning by default. */
    readonly icon?: IconName
}

/**
 * Full-page error/empty surface — GH's ErrorContent shape (message, optional
 * detail line, action links/buttons, graphic). Shared by NotFoundPage (404)
 * and the generic ErrorPage (route/load-failure recovery). `centered` is GH's
 * CenteredMessage shape: warning icon, title and glass buttons, no graphic.
 */
export function ErrorSurface(props: ErrorSurfaceProps) {
    if (props.centered) return CenteredErrorSurface(props)
    return (
        <div className="errorSurface">
            <p className="errorSurfaceMessage">{props.message}</p>
            {props.detail != null ? <p className="errorSurfaceDetail">{props.detail}</p> : null}
            {...props.actions.map((action) => renderAction(action, 'textLink'))}
            <img
                className="errorSurfaceImage"
                src={ERROR_HEADPHONES_LOGO_URL}
                alt=""
                aria-hidden="true"
            />
        </div>
    )
}

function CenteredErrorSurface(props: ErrorSurfaceProps) {
    return (
        <div className="errorSurface errorSurface--centered">
            <div className="centeredMessageCard">
                <Icon
                    icon={props.icon ?? 'warning'}
                    className="errorSurfaceIcon"
                    aria-hidden="true"
                />
                <p
                    className="errorSurfaceMessage centeredMessageTitle"
                    role="heading"
                    aria-level="1"
                >
                    {props.message}
                </p>
                {props.detail != null ? <p className="errorSurfaceDetail">{props.detail}</p> : null}
                <div className="errorSurfaceActions centeredMessageActions">
                    {...props.actions.map((action) => renderAction(action, 'textButton neutral'))}
                </div>
            </div>
        </div>
    )
}

function renderAction(action: ErrorSurfaceAction, className: string) {
    if ('href' in action) {
        return (
            <Link
                className={`errorSurfaceAction ${className}`}
                href={action.href}
                {...(action.navigateOptions ? { navigateOptions: action.navigateOptions } : null)}
            >
                {action.label}
            </Link>
        )
    }
    return (
        <button className={`errorSurfaceAction ${className}`} onclick={action.onClick}>
            {action.label}
        </button>
    )
}
